import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PaperPlaneTilt, Sparkle, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";

const SUGGESTIONS = [
  "How do I create my first post?",
  "How does the approval process work?",
  "How do I connect my social accounts?",
  "What can I do in my role?",
];

// Lightweight markdown-lite renderer for **bold** and "- " bullet lines.
function RichReply({ text }) {
  const blocks = [];
  let bullets = [];
  const flush = () => { if (bullets.length) { blocks.push({ type: "ul", items: bullets }); bullets = []; } };
  (text || "").split("\n").forEach((line) => {
    const t = line.trim();
    if (t.startsWith("- ") || t.startsWith("* ")) bullets.push(t.slice(2));
    else { flush(); if (t) blocks.push({ type: "p", text: t }); else blocks.push({ type: "br" }); }
  });
  flush();
  const fmt = (s, key) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={`${key}-${i}`} className="font-semibold">{p.slice(2, -2)}</strong>
      : <span key={`${key}-${i}`}>{p}</span>);
  return (
    <div className="space-y-1.5">
      {blocks.map((b, i) =>
        b.type === "ul" ? <ul key={i} className="list-disc pl-4 space-y-1">{b.items.map((it, j) => <li key={j}>{fmt(it, `${i}-${j}`)}</li>)}</ul>
        : b.type === "br" ? <div key={i} className="h-1" />
        : <p key={i}>{fmt(b.text, i)}</p>)}
    </div>
  );
}

// Conversational onboarding guide powered by Shee (grounded in the app knowledge base).
export function AskShee({ compact = false }) {
  const api = useApi();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/help/ask", { params: { session_id: "help" } })
      .then((r) => setMessages(r.data?.transcript || []))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", pending: true }]);
    setSending(true);
    try {
      const r = await api.post("/help/ask", { message: msg, session_id: "help" });
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: r.data.reply }; return c; });
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: e?.response?.data?.detail || "Shee is unavailable right now. Please try again.", error: true }; return c; });
    } finally { setSending(false); }
  };

  const reset = async () => {
    setMessages([]);
    try { await api.post("/help/ask/reset", null, { params: { session_id: "help" } }); } catch (e) { /* non-blocking */ }
  };

  return (
    <div className="flex flex-col" data-testid="ask-shee" style={{ height: compact ? "60vh" : "calc(100vh - 16rem)", minHeight: 380 }}>
      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-white border border-[#e5e7eb] rounded-[12px] p-5 space-y-4" data-testid="ask-shee-messages">
        {loadingHistory && (
          <p className="text-center text-sm text-[#7A9090] py-8" data-testid="ask-shee-history-loading">Loading your conversation…</p>
        )}
        {!loadingHistory && messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-4">
            <div className="w-12 h-12 rounded-[14px] bg-[#5FA8B4] flex items-center justify-center text-white">
              <Sparkle size={26} weight="fill" />
            </div>
            <p className="text-[#3C4048] font-semibold" style={{ fontFamily: "Montserrat" }}>Hi, I'm Shee — your onboarding guide</p>
            <p className="text-[#55666b] text-sm max-w-sm">Ask me anything about how the app works and I'll walk you through it.</p>
            <div className="flex flex-col gap-2 w-full max-w-sm">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} data-testid={`ask-shee-suggestion-${i}`} onClick={() => send(s)}
                  className="text-left text-sm text-[#3A7A8C] bg-[#f0f7f8] hover:bg-[#e6f2f4] rounded-[10px] px-3 py-2 transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ask-shee-msg-${i}`}>
            <div className={`max-w-[85%] rounded-[14px] px-4 py-3 text-sm ${m.role === "user" ? "bg-[#5FA8B4] text-white whitespace-pre-line" : m.error ? "bg-[#fef2f2] text-[#b91c1c] border border-[#fecaca]" : "bg-[#f5f7f7] text-[#3C4048]"}`}>
              {m.role === "assistant" && !m.error && m.content
                ? <RichReply text={m.content} />
                : (m.content || (m.pending ? <span className="text-[#64757b]">Thinking…</span> : ""))}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex items-end gap-2">
        {messages.length > 0 && (
          <Button type="button" onClick={reset} variant="outline" data-testid="ask-shee-reset" title="Start over"
            className="border-[#e5e7eb] text-[#7A9090] hover:bg-[#f5f7f7] rounded-[12px] h-[48px] w-[48px] p-0 shrink-0">
            <ArrowClockwise size={18} />
          </Button>
        )}
        <Textarea data-testid="ask-shee-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask Shee how something works…"
          className="bg-white border-[#e5e7eb] text-[#3C4048] min-h-[48px] max-h-32 resize-none rounded-[12px]" />
        <Button type="submit" disabled={sending || !input.trim()} data-testid="ask-shee-send"
          className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[12px] h-[48px] px-4 gap-2 shrink-0">
          <PaperPlaneTilt size={18} weight="fill" />
        </Button>
      </form>
    </div>
  );
}
